import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { BadgeCheck, Camera, CircleAlert, ClipboardCheck, Leaf, MapPin, Ruler, Send, Share2, Sprout, UserRound, X } from 'lucide-react'
import { api } from '../lib/api'
import { photoError } from '../lib/photo'
import { attachmentFromFile, mutationId, queueMutation } from '../lib/offline'
import { Modal } from '../components/Modal'
import { conditionLabel, formatDate, formatDateTime } from '../lib/format'
import '../passport.css'

type Condition = 'healthy' | 'watch' | 'needs_care' | 'critical' | 'dead' | 'unknown'
type ShareState = 'idle' | 'copied' | 'shared' | 'error'

const choices: [Condition, string, string][] = [
  ['healthy', 'Looks healthy', 'Green or vigorous'],
  ['needs_care', 'Needs attention', 'Wilt, guard, or weeds'],
  ['critical', 'May be dying', 'Severe damage or dry'],
]

const imageFor = (observation: any) => observation?.r2_key_public ? `/api/media/${observation.r2_key_public}` : null
const conceptVisual = '/concept/himalayan-cedar-tree.png'

function readableCondition(value: unknown) {
  return typeof value === 'string' && value ? conditionLabel(value) : 'Unknown'
}

function observationTitle(observation: any) {
  if (observation.note === 'Baseline activation record') return 'Planting baseline'
  const label = readableCondition(observation.condition_reported)
  return label === 'Unknown' ? 'Verified observation' : label
}

export function TreePassportLive() {
  const { code = '' } = useParams()
  const [tree, setTree] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [notFound, setNotFound] = useState(false)
  const [open, setOpen] = useState(false)
  const [condition, setCondition] = useState<Condition>('healthy')
  const [note, setNote] = useState('')
  const [photo, setPhoto] = useState<File | null>(null)
  const [preview, setPreview] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [queued, setQueued] = useState(false)
  const [submissionMessage, setSubmissionMessage] = useState('')
  const [formError, setFormError] = useState('')
  const [busy, setBusy] = useState(false)
  const [shareState, setShareState] = useState<ShareState>('idle')
  const input = useRef<HTMLInputElement>(null)

  const loadRequest = useRef(0)

  async function load() {
    const requestId = ++loadRequest.current
    setLoading(true)
    setNotFound(false)
    try {
      const nextTree = await api.tree(code)
      if (requestId !== loadRequest.current) return
      setTree(nextTree)
      setLoadError('')
    } catch (error: any) {
      if (requestId !== loadRequest.current) return
      setLoadError(error instanceof Error ? error.message : String(error))
      setNotFound(error?.status === 404)
      setTree(null)
    } finally {
      if (requestId === loadRequest.current) setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    return () => { loadRequest.current += 1 }
  }, [code])
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview) }, [preview])

  const observations = useMemo(() => [...(tree?.observations || [])].sort((a, b) => Date.parse(a.observed_at) - Date.parse(b.observed_at)), [tree])
  const latest = observations[observations.length - 1]
  const baseline = observations[0]
  const hero = imageFor(latest) || imageFor(baseline)
  const speciesName = tree?.common_name || tree?.scientific_name || 'Tree record'
  const locationLabel = tree?.location_privacy === 'hidden'
    ? 'Location withheld'
    : [tree?.site, tree?.district].filter(Boolean).join(', ') || 'Location not listed'
  const status = tree?.current_condition || tree?.status || 'unknown'
  const planter = tree?.roles?.find((role: any) => role.role === 'planter')

  async function share() {
    const data = { title: `${speciesName} · Tree Passport`, text: `View the public record for tree ${code}.`, url: window.location.href }
    try {
      if (navigator.share) {
        await navigator.share(data)
        setShareState('shared')
      } else {
        await navigator.clipboard.writeText(window.location.href)
        setShareState('copied')
      }
    } catch (error: any) {
      if (error?.name === 'AbortError') return
      setShareState('error')
    }
  }

  function clearDraft() {
    setPhoto(null)
    setPreview('')
    setCondition('healthy')
    setNote('')
    setFormError('')
    setSubmissionMessage('')
    setSubmitted(false)
    setQueued(false)
    if (input.current) input.current.value = ''
  }

  function closeModal() {
    if (busy) return
    setOpen(false)
    clearDraft()
  }

  function openModal() {
    clearDraft()
    setOpen(true)
  }

  function choose(file?: File) {
    if (!file) return
    const issue = photoError(file)
    if (issue) {
      setFormError(issue)
      setPhoto(null)
      setPreview('')
      return
    }
    setFormError('')
    setPhoto(file)
    setPreview(URL.createObjectURL(file))
  }

  async function submit() {
    if (!photo || busy) return
    const issue = photoError(photo)
    if (issue) { setFormError(issue); return }
    setBusy(true)
    setFormError('')
    setSubmissionMessage('')
    setQueued(false)

    let locationData: { latitude: number; longitude: number; accuracy: number } | null = null
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 8_000 }))
      locationData = { latitude: position.coords.latitude, longitude: position.coords.longitude, accuracy: position.coords.accuracy }
    } catch {
      locationData = null
    }

    const id = mutationId()
    const payload = {
      treeCode: code,
      conditionReported: condition,
      note: note.trim() || null,
      photoName: photo.name,
      observedAt: new Date().toISOString(),
      source: 'public',
      location: locationData,
    }
    const attachment = attachmentFromFile(photo, 'full_tree')

    try {
      const result = await api.submitObservation(payload, id)
      try {
        await api.uploadMedia(photo, { observationId: result.observationId, purpose: 'full_tree' })
        setSubmissionMessage('Your photo was sent for verification.')
      } catch {
        await queueMutation('submit_observation', payload, id, attachment)
        setQueued(true)
        setSubmissionMessage('Your observation was saved. The photo will retry when connectivity returns.')
      }
      setSubmitted(true)
    } catch (error: any) {
      if (!navigator.onLine || error instanceof TypeError) {
        await queueMutation('submit_observation', payload, id, attachment)
        setQueued(true)
        setSubmissionMessage('Saved on this device. It will sync when connectivity returns.')
        setSubmitted(true)
      } else {
        setFormError(error instanceof Error ? error.message : String(error))
      }
    } finally {
      setBusy(false)
    }
  }

  if (loading) return <div className="public-passport public-passport--state"><main className="public-passport__main"><section className="passport-state"><span className="passport-state__icon"><Sprout size={22} /></span><h1>Loading tree passport</h1></section></main></div>

  if (!tree) return <div className="public-passport public-passport--state">
    <header className="public-passport__header"><Link to="/" className="passport-brand"><span><Sprout size={20} /></span><strong>Tree Passport</strong></Link></header>
    <main className="public-passport__main"><section className="passport-state passport-state--error"><span className="passport-state__icon"><CircleAlert size={22} /></span><h1>{notFound ? 'Tree record not found' : 'Tree record unavailable'}</h1><p>{notFound ? `No active tree is linked to ${code}.` : loadError || 'The public record could not be loaded right now.'}</p><button type="button" className="passport-button passport-button--secondary" onClick={() => void load()}>Try again</button></section></main>
  </div>

  return <div className="public-passport">
    <header className="public-passport__header">
      <Link to="/" className="passport-brand"><span><Sprout size={20} /></span><strong>Tree Passport</strong></Link>
      <div className="passport-share-wrap">
        <button type="button" className="passport-button passport-button--share" onClick={() => void share()} aria-label="Share record" aria-describedby="passport-share-feedback"><Share2 size={17} /><span>{shareState === 'copied' ? 'Link copied' : shareState === 'shared' ? 'Shared' : 'Share record'}</span></button>
        <span id="passport-share-feedback" className={`passport-share-feedback passport-share-feedback--${shareState}`} aria-live="polite">{shareState === 'error' ? 'Sharing is unavailable right now.' : shareState === 'copied' ? 'Link copied to clipboard.' : shareState === 'shared' ? 'Share sheet opened.' : ''}</span>
      </div>
    </header>

    <main className="public-passport__main">
      <section className="passport-identity" aria-labelledby="passport-title">
        <div className={`passport-photo-panel ${hero ? '' : 'passport-photo-panel--empty passport-photo-panel--concept'}`}>
          {hero ? <img src={hero} alt={`Verified photo of ${speciesName}`} /> : <div className="passport-concept-visual">
            <img src={conceptVisual} alt="Concept visual of a young Himalayan cedar in a Kashmir park" />
            <div className="passport-concept-visual__overlay">
              <span className="passport-concept-visual__label">Concept visual · tree photo direction</span>
              <strong>Meet this tree</strong>
              <span>No verified tree photo yet</span>
            </div>
          </div>}
          {hero && <span className="passport-photo-badge"><BadgeCheck size={15} /> Verified photo</span>}
        </div>

        <div className="passport-identity__content">
          <div className="passport-record-line"><span className="passport-record-label">PUBLIC TREE PASSPORT</span><span className="passport-code">{tree.code}</span></div>
          <div className="passport-title-row"><div><h1 id="passport-title">{speciesName}</h1>{tree.scientific_name && tree.scientific_name !== speciesName && <p className="passport-scientific"><em>{tree.scientific_name}</em>{tree.local_name ? ` · ${tree.local_name}` : ''}</p>}{!tree.scientific_name && tree.local_name && <p className="passport-scientific">{tree.local_name}</p>}</div><span className={`passport-status passport-status--${status}`}>{readableCondition(status)}</span></div>

          <div className="passport-facts" aria-label="Tree facts">
            <div><span className="passport-fact-icon"><MapPin size={17} /></span><span><small>PLANTING SITE</small><strong>{locationLabel}</strong></span></div>
            <div><span className="passport-fact-icon"><Ruler size={17} /></span><span><small>BASELINE HEIGHT</small><strong>{tree.initial_height_cm ? `${tree.initial_height_cm} cm` : 'Not recorded'}</strong></span></div>
            <div><span className="passport-fact-icon"><Leaf size={17} /></span><span><small>PLANTED</small><strong>{formatDate(tree.planted_at)}</strong></span></div>
            {planter && <div><span className="passport-fact-icon"><UserRound size={17} /></span><span><small>PUBLICLY CREDITED TO</small><strong>{planter.display_name || planter.label}</strong></span></div>}
          </div>

          <button type="button" className="passport-button passport-button--primary passport-button--wide" onClick={openModal}><Camera size={18} /> Add today’s photo</button>
          <p className="passport-note"><ClipboardCheck size={15} /> New observations are reviewed before they update this verified record.</p>
        </div>
      </section>

      <div className="passport-content-grid">
        <section className="passport-panel passport-timeline-panel" aria-labelledby="timeline-title">
          <div className="passport-panel-heading"><div><span className="passport-record-label">VERIFIED HISTORY</span><h2 id="timeline-title">Tree timeline</h2></div><span className="passport-count">{observations.length} {observations.length === 1 ? 'record' : 'records'}</span></div>
          {observations.length ? <ol className="passport-timeline">
            {observations.map((observation: any) => {
              const image = imageFor(observation)
              const publicNote = observation.note && observation.note !== 'Baseline activation record' ? observation.note : ''
              return <li className="passport-timeline-item" key={observation.id}>
                <span className="passport-timeline-marker" aria-hidden="true" />
                <article>
                  <div className="passport-timeline-heading"><div><h3>{observationTitle(observation)}</h3><time dateTime={observation.observed_at}>{formatDateTime(observation.observed_at)}</time></div><span className="passport-verified-label"><BadgeCheck size={14} /> Verified</span></div>
                  {image ? <img className="passport-timeline-photo" src={image} alt={`Verified tree photo from ${formatDate(observation.observed_at)}`} /> : <div className="passport-timeline-photo passport-timeline-photo--empty"><Camera size={18} /><span>No public photo</span></div>}
                  {observation.height_cm && <span className="passport-timeline-measure"><Ruler size={14} /> {observation.height_cm} cm</span>}
                  {publicNote && <p>{publicNote}</p>}
                </article>
              </li>
            })}
          </ol> : <div className="passport-empty"><ClipboardCheck size={22} /><strong>No verified observations yet</strong><span>The public record will appear here after the first review.</span></div>}
        </section>

        <aside className="passport-side" aria-label="Public record details">
          <section className="passport-panel"><div className="passport-panel-heading passport-panel-heading--compact"><div><span className="passport-record-label">RECORD DETAILS</span><h2>At a glance</h2></div></div><dl className="passport-record-list"><div><dt>Current state</dt><dd>{readableCondition(status)}</dd></div><div><dt>Planted</dt><dd>{formatDate(tree.planted_at)}</dd></div><div><dt>Planting spot</dt><dd>{tree.spot_code || 'Not listed'}</dd></div><div><dt>Campaign</dt><dd>{tree.campaign || 'Not listed'}</dd></div><div><dt>Location privacy</dt><dd>{tree.location_privacy || 'Approximate'}</dd></div></dl></section>

          {Array.isArray(tree.lineage) && tree.lineage.length > 1 && <section className="passport-panel"><div className="passport-panel-heading passport-panel-heading--compact"><div><span className="passport-record-label">PLANTING SPOT HISTORY</span><h2>Replacement history</h2></div></div><p className="passport-side-copy">This spot has {tree.lineage.length} recorded saplings. Earlier mortality remains in the record.</p><div className="passport-lineage">{tree.lineage.map((entry: any) => <div key={entry.id}><span>{formatDate(entry.planted_at)}</span><strong>{readableCondition(entry.status)}</strong></div>)}</div></section>}

          <section className="passport-panel passport-transparency"><span className="passport-transparency__icon"><Leaf size={18} /></span><div><h2>Transparent record</h2><p>Death, replacement, and missing inspections stay visible in the history.</p></div></section>
        </aside>
      </div>
    </main>

    <footer className="public-passport__footer"><span><Sprout size={15} /> Tree Passport</span><span>Public record · {tree.code}</span></footer>

    {open && <Modal label="Add today’s photo" onClose={closeModal}>
      <div className="passport-modal-content">
        <button type="button" className="sheet-close" onClick={closeModal} disabled={busy} aria-label="Close photo form"><X size={20} /></button>
        {submitted ? <div className="passport-modal-success" role="status">
          <span className="passport-modal-success__icon"><BadgeCheck size={28} /></span>
          <span className="passport-record-label">SUBMISSION RECEIVED</span>
          <h2>{queued ? 'Saved on this device' : 'Photo sent for review'}</h2>
          <p>{submissionMessage}</p>
          <button type="button" className="passport-button passport-button--primary passport-button--wide" onClick={closeModal}>Done</button>
        </div> : <>
          <div className="passport-modal-heading"><span className="passport-modal-icon"><Camera size={22} /></span><div><span className="passport-record-label">PUBLIC OBSERVATION</span><h2>Add today’s photo</h2><p>Share a current view of {speciesName} for review.</p></div></div>
          <fieldset className="passport-condition-fieldset"><legend>How does the tree look today?</legend><div className="passport-condition-options">{choices.map(([value, label, hint]) => <button type="button" key={value} className={`passport-condition ${condition === value ? 'is-selected' : ''}`} onClick={() => setCondition(value)} aria-pressed={condition === value}><span className={`passport-condition-dot passport-condition-dot--${value}`} /><span><strong>{label}</strong><small>{hint}</small></span></button>)}</div></fieldset>
          <button type="button" className={`passport-photo-picker ${photo ? 'has-photo' : ''}`} onClick={() => input.current?.click()} aria-describedby="photo-picker-help"><span className="passport-photo-picker__preview">{preview ? <img src={preview} alt="Selected observation preview" /> : <Camera size={24} />}</span><span><strong>{photo?.name || 'Choose a full-tree photo'}</strong><small id="photo-picker-help">Frame the trunk, canopy, and guard.</small></span></button>
          <input ref={input} className="passport-file-input" type="file" accept="image/*" capture="environment" onChange={(event) => choose(event.target.files?.[0])} aria-label="Choose a full-tree photo" />
          <label className="passport-field"><span>Short note <em>optional</em></span><textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Anything a reviewer should know?" /></label>
          <p className="passport-privacy-note"><MapPin size={16} /> Location may support verification. Exact coordinates are never shown publicly.</p>
          {formError && <p className="passport-form-error" role="alert">{formError}</p>}
          <button type="button" disabled={!photo || busy} className="passport-button passport-button--primary passport-button--wide" onClick={() => void submit()}><Send size={17} />{busy ? 'Submitting…' : 'Submit for verification'}</button>
        </>}
      </div>
    </Modal>}
  </div>
}

